import { useCallback, useEffect, useMemo, useState } from "react";
import { Boxes, ClipboardCheck, MapPinned, PackageCheck, RotateCcw, ScanLine, UploadCloud, Warehouse } from "lucide-react";
import { ErrorBanner, LoadingState, PageHeader, Panel, StatCard, StatusBadge } from "../components/Ui";
import { useAuth } from "../context/AuthContext";
import { goodsReceipts, pickups, warehouseName } from "../lib/demoData";
import { formatDateTime, loadWarehouseSnapshot, receiptStatusPresentation, type ScannerAgentSnapshot, type WarehouseSnapshot } from "../lib/warehouseData";
import { OrderLink } from "../components/OrderLink";
import type { WarehouseCode } from "../types";

type WarehouseChoice = { id: string; code: WarehouseCode; name: string };

const fallbackWarehouses: WarehouseChoice[] = [
  { id: "preview-ob", code: "OB", name: "Oberhausen" },
  { id: "preview-mz", code: "MZ", name: "Merzenich" }
];

function scannerPresentation(agent: ScannerAgentSnapshot | undefined) {
  if (!agent) return { label: "Nicht eingerichtet", tone: "neutral" as const, detail: "Noch keine Meldung eines Scanner-Agenten" };
  const heartbeat = new Date(agent.lastHeartbeatAt).getTime();
  if (!Number.isFinite(heartbeat) || Date.now() - heartbeat > 120_000) {
    return { label: "Offline", tone: "danger" as const, detail: `Letztes Signal ${formatDateTime(agent.lastHeartbeatAt)}` };
  }
  if (agent.status === "folder_unreachable") {
    return { label: "Ordner fehlt", tone: "warning" as const, detail: agent.lastError || "Scannerordner nicht erreichbar" };
  }
  if (agent.status === "error") {
    return { label: "Fehler", tone: "danger" as const, detail: agent.lastError || "Scanner-Agent meldet einen Fehler" };
  }
  return {
    label: "Online",
    tone: "success" as const,
    detail: agent.lastImportAt
      ? `Letzter Import ${formatDateTime(agent.lastImportAt)}${agent.lastImportFileName ? ` · ${agent.lastImportFileName}` : ""}`
      : `Verbunden seit ${formatDateTime(agent.lastHeartbeatAt)}`
  };
}

function WarehouseSelector({ warehouses, value, onChange }: {
  warehouses: WarehouseChoice[];
  value: WarehouseCode;
  onChange: (code: WarehouseCode) => void;
}) {
  return (
    <section className="warehouse-overview-switch" aria-label="Lagerübersicht auswählen">
      {warehouses.map((warehouse) => (
        <button
          type="button"
          key={warehouse.id}
          className={value === warehouse.code ? "warehouse-overview-option warehouse-overview-option--active" : "warehouse-overview-option"}
          aria-pressed={value === warehouse.code}
          onClick={() => onChange(warehouse.code)}
        >
          <span>{warehouse.code}</span>
          <div><strong>{warehouse.name}</strong><small>Eigene Lagerübersicht öffnen</small></div>
        </button>
      ))}
    </section>
  );
}

export function DashboardPage() {
  const { cloudConfigured, profile } = useAuth();
  const [snapshot, setSnapshot] = useState<WarehouseSnapshot | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<WarehouseCode>(profile?.warehouse?.code ?? "OB");
  const [loading, setLoading] = useState(cloudConfigured);
  const [error, setError] = useState("");

  const load = useCallback(async (silent = false) => {
    if (!cloudConfigured) return;
    if (!silent) setLoading(true);
    try {
      setSnapshot(await loadWarehouseSnapshot());
      setError("");
    } catch (loadError) {
      setError(`Übersicht konnte nicht geladen werden: ${loadError instanceof Error ? loadError.message : "Unbekannter Fehler"}`);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [cloudConfigured]);

  useEffect(() => {
    void load();
    const interval = window.setInterval(() => { void load(true); }, 30_000);
    return () => window.clearInterval(interval);
  }, [load]);

  const isWarehouseUser = profile?.role === "warehouse";
  const availableWarehouses = useMemo<WarehouseChoice[]>(() => {
    const source = snapshot?.warehouses.length ? snapshot.warehouses : fallbackWarehouses;
    if (!isWarehouseUser) return source;
    if (!profile?.warehouse) return [];
    return source.filter((warehouse) => warehouse.id === profile.warehouse_id || warehouse.code === profile.warehouse?.code);
  }, [isWarehouseUser, profile?.warehouse, profile?.warehouse_id, snapshot?.warehouses]);

  useEffect(() => {
    if (isWarehouseUser && profile?.warehouse?.code) {
      setSelectedWarehouse(profile.warehouse.code);
      return;
    }
    if (!availableWarehouses.some((warehouse) => warehouse.code === selectedWarehouse) && availableWarehouses[0]) {
      setSelectedWarehouse(availableWarehouses[0].code);
    }
  }, [availableWarehouses, isWarehouseUser, profile?.warehouse?.code, selectedWarehouse]);

  const activeWarehouse = availableWarehouses.find((warehouse) => warehouse.code === selectedWarehouse) ?? availableWarehouses[0] ?? null;
  const warehouseView = useMemo(() => {
    if (!snapshot || !activeWarehouse) return null;
    const today = new Date().toDateString();
    const receipts = snapshot.receipts.filter((receipt) => receipt.warehouse === activeWarehouse.code);
    const packages = snapshot.packages.filter((item) => item.warehouse === activeWarehouse.code);
    const warehousePickups = snapshot.pickups.filter((pickup) => pickup.warehouse === activeWarehouse.code);
    const easyTec = snapshot.easyTec.filter((item) => item.warehouse === activeWarehouse.code);
    return {
      receipts,
      latest: receipts.slice(0, 6),
      receiptsToday: receipts.filter((receipt) => new Date(receipt.receivedAt).toDateString() === today).length,
      stockedPackages: packages.filter((item) => item.status === "stocked").length,
      pickupsToday: warehousePickups.filter((pickup) => pickup.signedAt && new Date(pickup.signedAt).toDateString() === today).length,
      easyTecOpen: easyTec.filter((item) => item.status === "open").length,
      openReturns: new Set(receipts.filter((receipt) => receipt.returnStatus === "registered").map((receipt) => receipt.workOrderId)).size,
      occupiedLocations: new Set(packages.filter((item) => item.status === "stocked").map((item) => item.location)).size,
      pendingPrintJobs: snapshot.pendingPrintJobsByWarehouse[activeWarehouse.code],
      scannerAgent: snapshot.scannerAgents.find((agent) => agent.warehouseId === activeWarehouse.id)
    };
  }, [activeWarehouse, snapshot]);

  if (loading) return <><PageHeader title="Übersicht" description="Die Lagerübersicht wird entsprechend deiner Freigabe geladen." /><LoadingState label="Dashboard wird aus der Testcloud geladen …" /></>;

  if (!cloudConfigured) {
    const demoReceipts = goodsReceipts.filter((receipt) => receipt.warehouse === selectedWarehouse);
    const demoPickups = pickups.filter((pickup) => pickup.warehouse === selectedWarehouse);
    const storedPackages = demoReceipts.reduce((sum, receipt) => sum + receipt.packagesStored, 0);
    const easyTecOpen = demoReceipts.filter((receipt) => receipt.easyTecStatus === "offen").length;
    return <>
      <PageHeader title={`Übersicht ${warehouseName(selectedWarehouse)}`} description="Lokaler Vorschaumodus ohne Cloudverbindung." />
      <WarehouseSelector warehouses={fallbackWarehouses} value={selectedWarehouse} onChange={setSelectedWarehouse} />
      <section className="stat-grid">
        <StatCard label="Heute eingegangen" value={demoReceipts.length} hint={warehouseName(selectedWarehouse)} icon={PackageCheck} />
        <StatCard label="Packstücke im Lager" value={storedPackages} hint="Demodaten" icon={Boxes} tone="green" />
        <StatCard label="Heute abgeholt" value={demoPickups.length} hint="Demodaten" icon={ClipboardCheck} tone="orange" />
        <StatCard label="EasyTec offen" value={easyTecOpen} hint="Demodaten" icon={UploadCloud} tone="red" />
      </section>
    </>;
  }

  if (!activeWarehouse) {
    return <><PageHeader title="Übersicht" description="Keine Lagerfreigabe vorhanden." /><ErrorBanner message="Deinem Benutzer ist kein aktives Lager zugewiesen. Bitte einen Administrator informieren." /></>;
  }

  const latest = warehouseView?.latest ?? [];
  const scanner = scannerPresentation(warehouseView?.scannerAgent);
  return (
    <>
      <PageHeader title={`Übersicht ${activeWarehouse.name}`} description="Wareneingänge, Lagerstatus und offene Aufgaben dieses Lagers auf einen Blick." />
      {error && <ErrorBanner message={error} />}
      {isWarehouseUser ? (
        <div className="warehouse-scope-notice">
          <Warehouse size={22} />
          <div><strong>Fest zugewiesen: Lager {activeWarehouse.name}</strong><span>Dieses Lagerkonto sieht ausschließlich die serverseitig freigegebenen Daten von {activeWarehouse.name}.</span></div>
        </div>
      ) : (
        <WarehouseSelector warehouses={availableWarehouses} value={activeWarehouse.code} onChange={setSelectedWarehouse} />
      )}
      <section className="stat-grid">
        <StatCard label="Heute eingegangen" value={warehouseView?.receiptsToday ?? 0} hint={activeWarehouse.name} icon={PackageCheck} />
        <StatCard label="Packstücke im Lager" value={warehouseView?.stockedPackages ?? 0} hint={`${warehouseView?.pendingPrintJobs ?? 0} Etiketten offen`} icon={Boxes} tone="green" />
        <StatCard label="Heute abgeholt" value={warehouseView?.pickupsToday ?? 0} hint="Mit Unterschrift" icon={ClipboardCheck} tone="orange" />
        <StatCard label="EasyTec offen" value={warehouseView?.easyTecOpen ?? 0} hint="Upload ausstehend" icon={UploadCloud} tone={warehouseView?.easyTecOpen ? "red" : "green"} />
      </section>
      <section className="dashboard-grid">
        <Panel title={`Letzte Wareneingänge · ${activeWarehouse.name}`} description="Nur Lieferungen des ausgewählten beziehungsweise zugewiesenen Lagers.">
          <div className="record-list">
            {latest.length === 0 ? <div className="empty-state empty-state--compact"><strong>Noch keine Wareneingänge in {activeWarehouse.name}</strong></div> : latest.map((receipt) => {
              const presentation = receiptStatusPresentation(receipt);
              return <article className="record-row" key={receipt.id}>
                <span className="record-row__icon"><PackageCheck size={19} /></span>
                <span><strong><OrderLink orderNumber={receipt.orderNumber} /></strong><small>{receipt.supplier} · {receipt.department}</small></span>
                <span><strong>Lagerplatz {receipt.location}</strong><small>{formatDateTime(receipt.receivedAt)} · {receipt.stockedPackages} von {receipt.packageCount} im Lager</small></span>
                <StatusBadge tone={presentation.tone}>{presentation.label}</StatusBadge>
              </article>;
            })}
          </div>
        </Panel>
        <Panel title={`Lagerstatus · ${activeWarehouse.name}`} description="Kennzahlen bleiben vollständig vom anderen Lager getrennt.">
          <div className="site-list warehouse-status-list">
            <article><span><MapPinned size={18} /></span><div><strong>{warehouseView?.occupiedLocations ?? 0} belegte Lagerplätze</strong><small>Mit mindestens einem eingelagerten Packstück</small></div><StatusBadge tone="success">Aktuell</StatusBadge></article>
            <article><span><RotateCcw size={18} /></span><div><strong>{warehouseView?.openReturns ?? 0} offene Retouren</strong><small>Zur Abholung angemeldete Aufträge</small></div><StatusBadge tone={warehouseView?.openReturns ? "warning" : "success"}>{warehouseView?.openReturns ? "Offen" : "Keine"}</StatusBadge></article>
            <article><span><ScanLine size={18} /></span><div><strong>Netzwerkscanner {warehouseView?.scannerAgent?.deviceName || ""}</strong><small>{scanner.detail}</small></div><StatusBadge tone={scanner.tone}>{scanner.label}</StatusBadge></article>
            <article><span><Warehouse size={18} /></span><div><strong>Zugriff {activeWarehouse.code}</strong><small>{isWarehouseUser ? "Fest durch Benutzerzuweisung begrenzt" : "Von Dispo oder Administration ausgewählt"}</small></div><StatusBadge tone="success">Geschützt</StatusBadge></article>
          </div>
        </Panel>
      </section>
    </>
  );
}
